export type ExperienceIndexCategoryId =
  | "all"
  | "business-administration"
  | "coaching-development"
  | "facility-program-operations"
  | "people-communication";

export type ExperienceIndexNamedCategoryId = Exclude<ExperienceIndexCategoryId, "all">;

export type ExperienceIndexCategory = {
  id: ExperienceIndexCategoryId;
  label: string;
  competencyNames: string[];
};

export type ExperienceIndexNamedCategory = Omit<ExperienceIndexCategory, "id"> & {
  id: ExperienceIndexNamedCategoryId;
};

// Keep in sync with membership-mobile-app `src/screens/SkillsIndexScreen/skillsIndexCategories.ts`
// until the Experience Index API exposes category ids.
export const EXPERIENCE_INDEX_CATEGORIES: ExperienceIndexNamedCategory[] = [
  {
    id: "business-administration",
    label: "Business & Administration",
    competencyNames: ["Admin", "Budget", "Financial Acumen", "Operator"]
  },
  {
    id: "people-communication",
    label: "People & Communication",
    competencyNames: ["Personnel", "Network", "Public Speaking", "Social Media"]
  },
  {
    id: "coaching-development",
    label: "Coaching & Development",
    competencyNames: ["Teaching and Coaching", "Golf Academy", "Technology"]
  },
  {
    id: "facility-program-operations",
    label: "Facility & Program Operations",
    competencyNames: ["Course Maintenance", "Food & Beverage", "Merchandise", "Tournament Operations"]
  }
];

export const ALL_EXPERIENCE_INDEX_CATEGORY: ExperienceIndexCategory = {
  id: "all",
  label: "All",
  competencyNames: EXPERIENCE_INDEX_CATEGORIES.flatMap((category) => category.competencyNames)
};

export const EXPERIENCE_INDEX_NAV_CATEGORIES: ExperienceIndexCategory[] = [
  ALL_EXPERIENCE_INDEX_CATEGORY,
  ...EXPERIENCE_INDEX_CATEGORIES
];

// Live API names that do not match EXPERIENCE_INDEX_CATEGORIES after trim+lowercase.
const EXPERIENCE_INDEX_COMPETENCY_NAME_ALIASES: Record<string, string> = {
  "food and beverage": "food & beverage"
};

export function normalizeExperienceCompetencyName(name: string) {
  const normalizedName = name.trim().toLowerCase();
  return EXPERIENCE_INDEX_COMPETENCY_NAME_ALIASES[normalizedName] ?? normalizedName;
}

export function getExperienceIndexCategoryId(competencyName: string): ExperienceIndexCategoryId | undefined {
  const normalizedName = normalizeExperienceCompetencyName(competencyName);

  return EXPERIENCE_INDEX_CATEGORIES.find((category) =>
    category.competencyNames.some((name) => normalizeExperienceCompetencyName(name) === normalizedName)
  )?.id;
}

export function getExperienceIndexCategoryLabel(competencyName: string) {
  const categoryId = getExperienceIndexCategoryId(competencyName);
  return categoryId ? EXPERIENCE_INDEX_CATEGORIES.find((category) => category.id === categoryId)?.label : undefined;
}

export function getExperienceIndexCategoryById(id: ExperienceIndexCategoryId) {
  return EXPERIENCE_INDEX_NAV_CATEGORIES.find((category) => category.id === id) ?? EXPERIENCE_INDEX_CATEGORIES[0];
}
